import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { storeGetOrDefault, DEF_NAV } from '../store';
import LanguageSelector from './LanguageSelector';
import './Navbar.css';

const getVisibleNav = () => {
  const stored = storeGetOrDefault('navItems', DEF_NAV);
  // Merge any new default items (by id) that are missing from stored nav
  const storedIds = new Set(stored.map(n => n.id));
  const merged = [...stored, ...DEF_NAV.filter(n => !storedIds.has(n.id))];
  return merged.filter(n => !n.disabled);
};

const Navbar = ({ cartCount, darkMode, toggleDark }) => {
  const [isMenuOpen, setIsMenuOpen]   = useState(false);
  const [visibleItems, setVisibleItems] = useState(getVisibleNav);

  useEffect(() => {
    const update = () => setVisibleItems(getVisibleNav());
    // same-tab updates (admin toggles nav item)
    window.addEventListener('zinga-store-update', update);
    // cross-tab updates (admin in another tab)
    window.addEventListener('storage', update);
    return () => {
      window.removeEventListener('zinga-store-update', update);
      window.removeEventListener('storage', update);
    };
  }, []);

  const closeMenu = () => setIsMenuOpen(false);

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <Link to="/" className="navbar-logo" onClick={closeMenu}>
          {/* SVG LOGO — inline so no image file needed */}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 220 62" height="42" style={{width:'auto'}} aria-label="Prabhjot Industry">
            <defs>
              <linearGradient id="nb-lf1" x1="1" y1="0" x2="0.2" y2="1">
                <stop offset="0%" stopColor="#3dc9a8"/>
                <stop offset="55%" stopColor="#26987c"/>
                <stop offset="100%" stopColor="#196a56"/>
              </linearGradient>
              <linearGradient id="nb-lf2" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#0f4e3e"/>
                <stop offset="100%" stopColor="#196a5600" stopOpacity="0"/>
              </linearGradient>
            </defs>
            {/* Leaf outline */}
            <path d="M28 57 C20 49 10 37 10 24 C10 11 18 2 28 2 C38 2 48 11 48 24 C48 38 38 50 28 57Z" fill="url(#nb-lf1)"/>
            {/* Left shadow for depth */}
            <path d="M28 57 C21 49 13 39 12 27 C11 15 16 6 23 3 C17 8 13 18 14 30 C15 43 22 53 28 57Z" fill="url(#nb-lf2)"/>
            {/* P letterform */}
            <g stroke="white" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" fill="none">
              <line x1="21" y1="14" x2="21" y2="45"/>
              <path d="M21 14 C31 14 35 19 35 25 C35 31 31 33 21 33"/>
            </g>
            {/* PRABHJOT text */}
            <text x="60" y="39" fontFamily="'Inter','Helvetica Neue',Arial,sans-serif" fontSize="22" fontWeight="800" letterSpacing="2.5" fill="#1e1e1e">PRABHJOT</text>
          </svg>
          {/* BACKUP TEXT LOGO — uncomment below and remove SVG above to restore
          <span className="logo-text">
            <span className="logo-primary">PRABJOT</span>
            <span className="logo-secondary">INDUSTRY</span>
          </span>
          */}
        </Link>

        <button
          className={`menu-toggle ${isMenuOpen ? 'open' : ''}`}
          onClick={() => setIsMenuOpen(o => !o)}
          aria-label="Toggle menu"
        >
          <span></span><span></span><span></span>
        </button>

        <ul className={`navbar-menu ${isMenuOpen ? 'active' : ''}`}>
          {visibleItems.map(item => (
            <li key={item.id}>
              <NavLink
                to={item.href}
                className={({ isActive }) => `navbar-link${isActive ? ' active' : ''}`}
                end={item.href === '/'}
                onClick={closeMenu}
              >
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <button
          className="dark-toggle"
          onClick={toggleDark}
          aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          title={darkMode ? 'Light mode' : 'Dark mode'}
        >
          <span className="toggle-icon">
            {darkMode ? (
              /* Sun icon */
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <circle cx="12" cy="12" r="5"/>
                <line x1="12" y1="1" x2="12" y2="3"/>
                <line x1="12" y1="21" x2="12" y2="23"/>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                <line x1="1" y1="12" x2="3" y2="12"/>
                <line x1="21" y1="12" x2="23" y2="12"/>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
              </svg>
            ) : (
              /* Moon icon */
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
            )}
          </span>
          <span className="toggle-label">{darkMode ? 'Light' : 'Dark'}</span>
        </button>
        <LanguageSelector />
      </div>
    </nav>
  );
};

export default Navbar;
